<script lang="ts">
    import {type Attendee, bunkTypes} from "@shared/Types.ts";
    import {onMount} from "svelte";
    import {email, fetchOrigin, page, registration, payments, serverConfig} from "../data.ts";
    import {calculateCost, type PricingInfo, renderPricingInfo} from "@shared/calculate-cost";
    import {sum, toFixed} from "@shared/Utils.ts";
    import AttendeeRow from "../AttendeeRow.svelte";
    import Warnings from "../Warnings.svelte";
    import SaveRegistrationButton, {save} from "../SaveRegistrationButton.svelte";
    import RegistrationConfirmation from "../RegistrationConfirmation.svelte";


    let loaded = false;
    let pricingInfo: PricingInfo;
    $: pricingInfo = calculateCost($registration, $payments);
    let totalPaid = 0;
    $: totalPaid = sum(...($payments?.payments ?? []).map(e => e.amount))

    let paymentProcessing = false;

    onMount(async () => {
        console.log($email)
        if (!$email) {
            page.set('EnterEmail');
        }
        const result = await fetchOrigin('/registration');
        const json = await result.json();
        if (json.error) {
            page.set('EnterEmail');
        }
        $registration = json;
        $registration.email = $email;
        const paymentsResult = await fetchOrigin('/payments');
        $payments = await paymentsResult.json();
        loaded = true;
        // ?fromPaymentPage=1  if so, add paymentProcessing=1 and remove the url param from the parameter
        const fromPaymentPage = new URLSearchParams(window.location.search).get('fromPaymentPage');
        if (fromPaymentPage) {
            paymentProcessing = true;
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    })

    function createAttendee(): Attendee {
        return {
            lastName: '',
            firstName: '',
            ageOnApril24: '18 or over',
            registeredForRE: false,
            responsibleAdult: '',
            vegetarian: false,
            vegan: false,
            glutenFree: false,
            dairyFree: false,
        };
    }

    function addAttendee() {
        if (!$registration.attendees) {
            $registration.attendees = [];
        }
        const lastAttendee: Attendee | undefined = $registration.attendees.at(-1);
        if (lastAttendee) {
            $registration.attendees = [...$registration.attendees, JSON.parse(JSON.stringify(lastAttendee)) as Attendee];
        } else {
            let attendee = createAttendee();
            attendee.lastName = $registration.lastName;
            attendee.firstName = $registration.firstName;
            $registration.attendees = [attendee];
        }
    }

    function removeAttendee(i: number) {
        registration.update((r) => {
            const attendees = r.attendees;
            if (attendees) {
                attendees.splice(i, 1);
            }
            return r;
        })
    }

    let confirmMsg = '';
    async function confirm(t: 'free' | 'paid' | 'waitlist') {
        confirmMsg = '';
        await save();
        // validation happens on the server side.
        const result = await fetchOrigin('/confirm/' + t);
        const data = await result.json();
        if (data.error) {
            confirmMsg = data.error;
            return;
        }
        if (data.redirect) {
            window.location.href = data.redirect;
            confirmMsg = 'taking you to payment page...';
            return;
        }
        if (data.success) {
            const paymentsResult = await fetchOrigin('/payments');
            $payments = await paymentsResult.json();
            return;
        }
        confirmMsg = 'Unknown error. Please contact woballsouls@gmail.com.';
    }

    async function unconfirm() {
        await fetchOrigin('/unconfirm');
        const paymentsResult = await fetchOrigin('/payments');
        $payments = await paymentsResult.json();
    }
</script>

{#if !loaded}
    <div style="display: flex; justify-content: center;">
        <span aria-busy="true">Loading your registration</span>
    </div>
{:else if $payments?.confirmed && registration}
    <RegistrationConfirmation r={$registration} payments={$payments} {pricingInfo} {unconfirm}/>
{:else if $registration}
    {#if paymentProcessing}
        <div class="alert alert-warning">Your payment is processing. Please refresh this page in
            a few seconds. If you do not see your payment, please contact Susan Milner at
            woballsouls@gmail.com.
        </div>
    {/if}
    <Warnings/>

    <article>
        <h3>Instructions</h3>
        <p>Click "save registration" at any time, that way you can come
            back to it later. Your registration will only be complete after confirmation.</p>
    </article>
    {#if $serverConfig.noNewRegistrations}
        <article class="alert alert-warning">
            No new registrations are being accepted. You may edit your existing registration.
        </article>
    {:else if $serverConfig?.waitlistOnly}
        <article class="alert alert-warning">
            Registration is currently full. You can still register to be added to the waitlist.
        </article>
    {/if}

    <article>
        <h3>Part 1 – Who Are You</h3>
        <label>
            Email
            <input type="email" readonly value={$email} autocomplete="off" aria-label="Read-only input"/>
            <small>You cannot change the email associated with your registration.</small>
        </label>
        <label>
            First Name
            <input bind:value={$registration.firstName} autofocus aria-invalid={$registration?.firstName?.length < 2}/>
            {#if $registration?.firstName?.length < 2}
                <small>First name must be at least two characters</small>
            {/if}
        </label>
        <label>
            Last Name
            <input bind:value={$registration.lastName}  aria-invalid={$registration?.lastName?.length < 2}/>
            {#if $registration?.lastName?.length < 2}
                <small>Last name must be at least two characters</small>
            {/if}
        </label>
        <label>
            Phone Number
            <input type='tel' bind:value={$registration.phoneNumber} aria-invalid={$registration?.phoneNumber?.replace(/[^0-9]/g,"")?.length < 10}/>
        </label>
        <label>
            Preferred Contact Method
            <select bind:value={$registration.preferredContactMethod}>
                <option value="email">Email</option>
                <option value="text">Text</option>
            </select>
        </label>
        <SaveRegistrationButton/>
    </article>

    <article>
        <h3>Part 2 – Who Is Coming</h3>
        <p>All children and youth must have a responsible adult on-site,
            and children through elementary school must be appropriately
            supervised at all times.   Please include the name of the on-site
            responsible adult.  By filling out that box you are stating that you
            understand this requirement.
        </p>
        <div style="overflow-x: scroll;">
            <table>
                <thead>
                <tr>
                    <th></th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Age on April 24th</th>
                    <th>Registered for RE</th>
                    <th>Responsible Adult</th>
                    <th>Vegetarian</th>
                    <th>Vegan</th>
                    <th>Gluten-Free</th>
                    <th>Dairy-Free</th>
                </tr>
                </thead>
                <tbody>
                {#each ($registration?.attendees ?? []) as attendee, i}
                    <AttendeeRow attendee={attendee} on:remove={() => removeAttendee(i)} on:change={(value) => $registration.attendees[i] = value.detail}/>
                {/each}
                </tbody>
            </table>
        </div>
        {#if $registration?.attendees?.length === 0}
            <div class="alert alert-warning">
                You must add at least one attendee. (Yourself)
            </div>
        {/if}
        {#if $registration?.attendees?.length > new Set($registration?.attendees?.map(a => a.firstName + a.lastName)).size}
            <div class="alert alert-warning">
                Warning: Some attendees have the same name.
            </div>
        {/if}
        {#if $registration?.attendees?.some(a => a.ageOnApril24 === '')}
            <div class="alert alert-warning">
                You must enter an age for all attendees.
            </div>
        {/if}
        <div style="margin: 16px 0">
            <button on:click={addAttendee}>Add Attendee</button>
        </div>
        <label>
            Optionally add additional details about additional dietary needs.
            <textarea bind:value={$registration.additionalDietaryNeeds}></textarea>
        </label>
        <SaveRegistrationButton/>
    </article>

    <article>
        <h3>Part 3 – Sleeping Arrangements</h3>
        <p>All meals are included unless otherwise specified. If your group has specific sleeping requests, detail them in the Cabin Sharing Preferences.</p>
        <label>
            Select Your Sleeping Arrangements (Required)
            <select bind:value={$registration.sleepingArrangementChoice} required aria-invalid={!$registration.sleepingArrangementChoice}>
                <option value="" disabled selected>Select an option</option>
                {#each Object.entries(bunkTypes) as [k, v]}
                    <option value={k}>{v}</option>
                {/each}
            </select>
        </label>

        <label>
            Enter your cabin sharing preferences, if applicable
            <textarea bind:value={$registration.cabinSharing}></textarea>
            <small>If there is someone who would you like to share a cabin with, mention it here.</small>
        </label>

        <label>
            Enter any comments or special requests for sleeping arrangements
            <textarea bind:value={$registration.sleepingArrangementsComments}></textarea>
        </label>
        <SaveRegistrationButton/>
    </article>

    <article>
        <h3>Part 4 – Additional Accommodations, Ability to Pay</h3>
        <label>
            Additional comments about your registration:
            <textarea bind:value={$registration.registrationComments}></textarea>
        </label>
        <label>
            Ride Sharing
            <select bind:value={$registration.rideSharing}>
                <option value=""></option>
                <option value="I need a ride">I need a ride</option>
                <option value="I can offer a ride">I can offer a ride</option>
            </select>
            <small>Leave blank if none applies.</small>
        </label>
        <p>Everyone can come regardless of their ability to pay. What best describes you at this time:</p>
        <label>
            What best describes you at this time: (Required)
            <select bind:value={$registration.abilityToPay} required>
                <option value="I can pay my fair share">I can pay my fair share</option>
                <option value="I can pay more than my fair share">I can pay more than my fair share</option>
                <option value="I can pay a reduced rate">I can pay a reduced rate</option>
                <option value="Please waive my registration fees">Please waive my registration fees</option>
                <option value="Specialized Fees">Specialized Fees</option>
            </select>
        </label>
        {#if $registration.abilityToPay === 'Please waive my registration fees'}
            <label>
                If you requested a waiver, please describe why
                <textarea bind:value={$registration.waiverReason} required aria-invalid={!$registration.waiverReason}></textarea>
            </label>
        {:else if $registration.abilityToPay === 'I can pay more than my fair share'}
            <label>
                Donation Amount:
                <input type="number" bind:value={$registration.donationAmount} min="0" placeholder="Donation Amount"/>
                <small>Thank you for choosing to donate to Weekend on the Bay!</small>
            </label>
        {/if}
        <SaveRegistrationButton/>
    </article>

    <article>
        <h3>Part 5 – Participant Waiver</h3>
        <p><a href="https://docs.google.com/document/d/11UhN1CEXJ7Lm32n1FDfIHVBiXW-BjbUeZY6jz7CO0BU/edit?tab=t.0" target="_blank">Please click here to read the waiver.</a> (Opens in new tab)</p>
        <label>
            <input type="checkbox" bind:checked={$registration.liabilityWaiverAccepted} required aria-invalid={!$registration.liabilityWaiverAccepted}/>
            I Accept the Waiver (Required)
        </label>
        <label>
            Full Name: (Required)
            <input bind:value={$registration.liabilityWaiverAcceptedBy} required/>
        </label>
        <SaveRegistrationButton/>
    </article>

    <article>
        <h3>Part 6 – Registration Fees</h3>
        {@html renderPricingInfo($registration, pricingInfo, $payments)}
        <SaveRegistrationButton/>
    </article>

    {@const balance = pricingInfo.total - totalPaid}
    {@const hasRegisteredBefore = $payments.payments.length > 0}
    <article>
        <h3>Part 7 - Confirmation</h3>
<!--        <div class="alert alert-warning">-->
<!--            Payment and registration confirmation is in the works.-->
<!--            Payment and confirmation will be available by <s>Tuesday March 3rd</s> we are continuing to work with the church to get payment ready.-->
<!--            Please save your registration and watch your email for a notification to pay. Thank you!-->
<!--        </div>-->
        {#if $serverConfig?.waitlistOnly && !hasRegisteredBefore}
            <p>
                Registration is full. Please confirm your registration to be added to the waitlist.
            </p>
            {#if confirmMsg}
                <div class="alert alert-warning">{confirmMsg}</div>
            {/if}
            <button on:click={() => confirm('waitlist')}>Confirm Waitlist Registration</button>
        {:else}
            {#if balance < 0.5}
                <p>
                    Please confirm registration.
                </p>
                {#if confirmMsg}
                    <div class="alert alert-warning">{confirmMsg}</div>
                {/if}
                <button on:click={() => confirm('free')}>Confirm Registration - No Payment Due</button>
            {/if}
            {#if balance > 0 && Math.abs(balance) > 0.50}
                <p>
                    Your calculated Registration Fees balance is ${toFixed(Math.abs(balance), 2)}.
                </p>
                <p>
                    If you feel that this balance is incorrect, please contact Susan Milner at woballsouls@gmail.com.
                    Otherwise, to make a payment, click the Make Payment button.
                    You will be taken to a Stripe screen where you can make a payment.
                </p>
                {#if confirmMsg}
                    <div class="alert alert-warning">{confirmMsg}</div>
                {/if}
                <button on:click={() => confirm('paid')}>Confirm Registration - Make Payment</button>
            {:else if balance < 0 && Math.abs(balance) > 0.50}
                <p>It seems that you are due a refund of ${Math.abs(balance)}. Please contact woballsouls@gmail.com to request your refund.</p>
            {/if}
        {/if}
        <p>We can provide full refunds (minus transaction fees) through March 23rd.  Partial refunds will be available March 23rd through April 9th.   Unfortunately we can not provide refunds after April 9th.  Please plan accordingly. </p>
        <h5>Payment History</h5>
        <table>
            <thead>
                <tr>
                    <th>Amount</th>
                    <th>Date</th>
                </tr>
            </thead>
            <tbody>
                {#each $payments?.payments ?? [] as payment}
                    <tr>
                        <td style="text-align: center;">{payment.amount === 0 ? 'free confirmation' : ('$' + toFixed(payment.amount, 2))}</td>
                        <td style="text-align: center;">{new Date(payment.date).toLocaleString()}</td>
                    </tr>
                {:else}
                    <tr>
                        <td colspan="2" style="text-align: center;">No payments yet.</td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </article>
{/if}

<style>
    th {
        text-align: center;
        white-space: nowrap;
    }
</style>